/**
 * Phone number formatting and validation utilities for North American numbers
 */

/**
 * Formats a phone number string to North American format: +1 (###) ###-####
 * @param value - Raw phone number input
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // If the value already starts with '+1', extract only the digits after '+1'
  // This prevents the '1' from '+1' being treated as part of the phone number
  let digits = ''

  if (value.startsWith('+1')) {
    // Extract digits after '+1'
    digits = value.slice(2).replace(/\D/g, '')
  } else {
    // Remove all non-numeric characters from the entire input
    digits = value.replace(/\D/g, '')
  }

  // Handle different input lengths
  if (digits.length === 0) return ''

  // Limit to 10 digits max for North American numbers
  digits = digits.slice(0, 10)

  // Format based on length
  if (digits.length <= 3) {
    return `+1 (${digits}`
  } else if (digits.length <= 6) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
  } else {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
}

/**
 * Validates a North American phone number
 * @param phoneNumber - Phone number to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export const validatePhoneNumber = (
  phoneNumber: string
): { isValid: boolean; error?: string } => {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '')

  // Empty is valid (optional field)
  if (cleaned.length === 0) {
    return { isValid: true }
  }

  // Check for valid North American number lengths
  // 10 digits: ###-###-####
  // 11 digits: 1-###-###-#### (with country code)
  if (cleaned.length === 10) {
    // Check if area code and exchange are valid (not starting with 0 or 1)
    const areaCode = cleaned.slice(0, 3)
    const exchange = cleaned.slice(3, 6)

    if (areaCode[0] === '0' || areaCode[0] === '1') {
      return { isValid: false, error: 'Area code cannot start with 0 or 1' }
    }

    if (exchange[0] === '0' || exchange[0] === '1') {
      return { isValid: false, error: 'Exchange code cannot start with 0 or 1' }
    }

    return { isValid: true }
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    // Same validation for 11-digit numbers starting with 1
    const areaCode = cleaned.slice(1, 4)
    const exchange = cleaned.slice(4, 7)

    if (areaCode[0] === '0' || areaCode[0] === '1') {
      return { isValid: false, error: 'Area code cannot start with 0 or 1' }
    }

    if (exchange[0] === '0' || exchange[0] === '1') {
      return { isValid: false, error: 'Exchange code cannot start with 0 or 1' }
    }

    return { isValid: true }
  } else if (cleaned.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' }
  } else {
    return { isValid: false, error: 'Invalid phone number format' }
  }
}

/**
 * Extracts raw digits from a formatted phone number
 * @param formattedNumber - Formatted phone number
 * @returns Raw digits only
 */
export const extractPhoneDigits = (formattedNumber: string): string => {
  return formattedNumber.replace(/\D/g, '')
}
